import React, { useState } from "react";
import ExcelJS from "exceljs";
import axiosInstance from "../services/axiosInstance";
import { Grid } from "@mui/system";
import { Button } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";

const UploadDentistPopup = ({ openPopUp, closePopUp }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState("");
  const [invalidUserList, setInvalidUserList] = useState([]);

  const RefreshPage = () => {
    setError(null);
    closePopUp();
  };

  if (openPopUp !== true) return null;

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    const maxSize = 10 * 1024 * 1024;
    setError(null);

    if (!file) {
      setError("No file selected.");
      setSelectedFile(null);
      setFileName(null);
      return;
    }

    if (file && file.size > maxSize) {
      setError("File size exceeds the 10MB limit.");
      e.target.value = "";
      return;
    }

    const isValidFile = file.name.toLowerCase().endsWith(".xlsx");
    if (isValidFile) {
      setSelectedFile(file);
      setFileName(file.name);
      setError(null);
      //console.log("File selected:", file);
    } else {
      setError("Please upload a valid .xlsx file");
      setSelectedFile(null);
      setFileName(null);
    }
  };

  const handleSave = async () => {
    if (!selectedFile) {
      setError("Please upload a valid .xlsx file");
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(false);
    setUploadedFileName("");

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const response = await axiosInstance.post(
        "/api/Dentist/uploadDentistList",
        formData,
        {
          headers: {},
        }
      );

      const invalidUserData = await response.invalidData.map((x) => {
        return { errorMessage: x.errorMessage, 
          dentistCode: x?.dentistCode,
          practiceName: x?.practiceName,
          dentistDisplayName: x?.dentistDisplayName  };
      });
      setInvalidUserList(invalidUserData);
      invalidUserData.length >= 1 && setError("Invalid Dentist below.");

      if (response.validData && response?.validData?.length > 0) {
        setSuccess(true);
        setUploadedFileName(
          `Successfully added ${response?.validData?.length} dentist to the system!`
        );
      }
    } catch (error) {
      setError(error?.response?.data || "Error uploading file.");
    } finally {
      setLoading(false);
    }
  };

  // Download Excel template
  const downloadDentistExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Dentist Data");

    // Define the headers for the dentist data template
    const headers = [
      "Dentist Code",
      "Practice Name",
      "Dentist Name",
      "Dentist Display Name",
      "Geo Code",
      "PP Notes",
    ];

    // Add the headers to the first row
    worksheet.addRow(headers);

    // Set column widths
    worksheet.columns = [
      { width: 15 }, // dentistCode
      { width: 20 }, // practiceName
      { width: 20 }, // dentistName
      { width: 25 }, // dentistDisplayName
      { width: 10 }, // geoCode
      { width: 30 }, // pP_Notes
    ];

    // Add data validation if required for specific fields
    worksheet.dataValidations.add("A2:A1000", {
      type: "textLength",
      operator: "greaterThan",
      formula1: 0,
      showError: true,
      errorTitle: "Invalid Entry",
      error: "dentist Code cannot be empty.",
    });

    worksheet.dataValidations.add("E2:E1000", {
      type: "textLength",
      operator: "greaterThan",
      formula1: 0,
      showError: true,
      errorTitle: "Invalid Entry",
      error: "geoCode cannot be empty.",
    });

    // Center align the header row
    const headerRow = worksheet.getRow(1);
    headerRow.eachCell((cell) => {
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.font = { bold: true };
    });

    // Generate and download the Excel file
    const excelBlob = await workbook.xlsx.writeBuffer();
    const excelUrl = URL.createObjectURL(
      new Blob([excelBlob], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      })
    );

    const link = document.createElement("a");
    link.href = excelUrl;
    link.download = "Dentist_Data_Template.xlsx";
    document.body.appendChild(link);
    link.click();

    URL.revokeObjectURL(excelUrl);
    document.body.removeChild(link);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const file = e.dataTransfer.files[0];
    if (file) {
      setFileName(file.name);
      setSelectedFile(file);
    }
  };

  return (
    <div
      id="ModelContainer"
      className="fixed inset-0 bg-black flex justify-center items-center bg-opacity-20 backdrop-blur-sm z-40"
    >
      <div className="relative bg-blue-400 w-5/6 xl:w-[400px] 3xl:w-[600px] text-center mx-auto rounded-[30px] p-10 pt-14 shadow-[0_0_7px_rgba(92,92,92,0.35)] drop-shadow-[0_0_4px_rgba(178,178,178,0.2)]">
        <div className="w-full xl:w-4/5 text-center mx-auto">
          <div className="flex items-center">
            <a
              onClick={downloadDentistExcel}
              className="w-full ml-2 inline-flex justify-between items-center px-3 py-2 border text-sm leading-4 font-medium rounded-lg shadow-sm text-black focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <span>Download sample file</span>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 48 48"
              >
                <path
                  d="M45.81,19.56a9.5,9.5,0,0,0-6.26-3.86q0-.44,0-0.88a12.72,12.72,0,0,0-25.29-2,13.34,13.34,0,0,0-2.22-.18C5.42,12.67,0,17.59,0,23.64S3.64,33.88,9.36,34.39l29.09,0C43.2,34.19,48,31.1,48,25.61A9.52,9.52,0,0,0,45.81,19.56Z"
                  fill="#aedff5"
                />
                <path
                  d="M31.52,38.62a2,2,0,0,0-2.82-.22L26,40.69V22.92a2,2,0,0,0-4,0V40.67l-2.7-2.31a2,2,0,1,0-2.61,3l6,5.13,0.06,0,0.12,0.09,0.22,0.13,0.14,0.06,0.25,0.08,0.12,0A2,2,0,0,0,24,47h0a2,2,0,0,0,.39,0l0.11,0,0.27-.09,0.06,0,0,0a2,2,0,0,0,.42-0.26l6-5.09A2,2,0,0,0,31.52,38.62Z"
                  fill="#38b1e7"
                />
              </svg>
            </a>
          </div>

          <hr className="my-4 border-gray-700" />

          <div className="flex items-start text-sm text-gray-600 pb-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 text-blue mr-1"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fill-rule="evenodd"
                d="M18 10c0 4.418-3.582 8-8 8s-8-3.582-8-8 3.582-8 8-8 8 3.582 8 8zm-8 3a1 1 0 100-2 1 1 0 000 2zm.75-5.25a.75.75 0 00-1.5 0v3a.75.75 0 001.5 0v-3z"
                clip-rule="evenodd"
              />
            </svg>
            <span>
              Single upload should not be more than 10MB, only{" "}
              <strong>.xlsx</strong> type is allowed.
            </span>
          </div>

          <div className="flex justify-between items-center space-x-4">
            <input
              id="fileInput"
              type="file"
              accept=".xlsx"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>

          <div
            className="flex items-center justify-center w-full"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <label
              htmlFor="dropzone-file"
              className="flex flex-col items-center justify-center w-full h-48 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-600"
            >
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <svg
                  className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 20 16"
                >
                  <path
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                  />
                </svg>
                {fileName ? (
                  <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                    <span className="font-semibold">{fileName}</span>
                  </p>
                ) : (
                  <>
                    <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                      <span className="font-semibold">Click to upload</span> or
                      drag and drop
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      xlsx
                    </p>
                  </>
                )}
              </div>
              <input
                id="dropzone-file"
                type="file"
                className="hidden"
                onChange={handleFileChange}
              />
            </label>
          </div>

          {loading && <p className="text-blue-500">Uploading...</p>}
          {error && (
            <>
              <p className="text-red">{error}</p>
              <Grid container spacing={1} direction="row" alignItems="center">
                {invalidUserList &&
                  invalidUserList.map((value, index) => (
                    <Grid item key={index}>
                      {/* Use applicationModuleId as the key if available */}
                      <Tooltip
                        title={
                          value.errorMessage && value.errorMessage.trim() !== ""
                            ? value.errorMessage
                            : "Invalid Data"
                        }
                        arrow
                      >
                        <Button
                          variant="solid"
                          size="small"
                          className="!bg-[#EFEFEF] !capitalize !rounded-md !leading-normal !text-2xl !px-[6px] !pb-[3px] !pt-[5px] !border-none !text-gray-500"
                        >
                          {value.dentistCode
                            ? value.dentistCode
                            : value.practiceName ||
                              value.dentistDisplayName ||
                              "Invalid dentist"}
                        </Button>
                      </Tooltip>
                    </Grid>
                  ))}
              </Grid>
            </>
          )}
          {success && (
            <div>
              <p className="text-green-500 pt-2">{uploadedFileName}</p>
            </div>
          )}

          <div className="flex justify-end mt-4">
            <div className="flex space-x-4">
              <button
                className="bg-white text-blue uppercase text-2xl xl:text-base 3xl:text-2xl border border-blue hover:bg-blue-800 hover:border-blue-800 hover:text-white font-robotoBold py-2 px-5 rounded-lg focus:outline-none focus:shadow-outline transition-all ease-in leading-relaxed"
                type="button"
                onClick={RefreshPage}
              >
                Abbrechen
              </button>
              <button
                className="bg-blue uppercase text-2xl xl:text-base 3xl:text-2xl border border-blue hover:bg-blue-800 hover:border-blue-800 text-white font-robotoBold py-2 px-5 rounded-lg focus:outline-none focus:shadow-outline transition-all ease-in leading-relaxed"
                type="button"
                onClick={handleSave}
              >
                Speichern
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadDentistPopup;
